import { useState } from 'react'
import { PageHead, Skel } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'
import {
  scopeHint, scopeLabel, useAPIKeys, useCreateAPIKey, useRevokeAPIKey,
  type CreatedAPIKey,
} from '../lib/apikeys'

const allScopes = ['read', 'write', 'admin']

export function ApiKeys() {
  const keys = useAPIKeys()
  const create = useCreateAPIKey()
  const revoke = useRevokeAPIKey()

  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['read'])
  const [created, setCreated] = useState<CreatedAPIKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const toggle = (scope: string) => {
    setScopes((cur) => {
      if (cur.includes(scope)) {
        // A key always keeps at least one scope; the server would default an empty list to read.
        return cur.length === 1 ? cur : cur.filter((s) => s !== scope)
      }
      return [...cur, scope]
    })
  }

  const closeCreated = () => { setCreated(null); setCopied(false) }

  const copyKey = async () => {
    if (!created) return
    try {
      await navigator.clipboard.writeText(created.key)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  const rows = keys.data ?? []

  return (
    <>
      <PageHead title="คีย์ API" sub="สำหรับเชื่อมต่อระบบภายนอก · คีย์เต็มแสดงเพียงครั้งเดียวตอนสร้าง" />

      <div className="grid-main">
        <div className="card card-pad">
          <div className="card-label">สร้างคีย์ใหม่</div>
          <div className="field">
            <label htmlFor="keyname">ชื่อคีย์</label>
            <input id="keyname" className="input" maxLength={80} placeholder="เช่น ระบบบัญชีภายนอก"
              value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="field">
            <label>สิทธิ์การใช้งาน</label>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              {allScopes.map((s) => (
                <button key={s} type="button" title={scopeHint[s]}
                  className={`chip${scopes.includes(s) ? ' chip-on' : ''}`}
                  onClick={() => toggle(s)}>
                  {scopeLabel[s]}
                </button>
              ))}
            </div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 10, lineHeight: 1.7 }}>
              {scopes.map((s) => `${scopeLabel[s]} — ${scopeHint[s]}`).join(' · ')}
            </div>
          </div>

          {create.isError ? (
            <div className="form-error" style={{ marginTop: 12 }}>
              {create.error instanceof Error ? create.error.message : 'สร้างคีย์ไม่สำเร็จ'}
            </div>
          ) : null}

          <button className="btn btn-primary" style={{ marginTop: 18 }}
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate({ name: name.trim(), scopes }, {
              onSuccess: (res) => { setCreated(res); setCopied(false); setName(''); setScopes(['read']) },
            })}>
            {create.isPending ? 'กำลังสร้าง…' : 'สร้างคีย์'}
          </button>
        </div>

        <div className="card card-pad">
          <div className="card-label">วิธีใช้</div>
          <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
            <li>ส่งคีย์ใน header <span className="mono">Authorization: Bearer aai_…</span></li>
            <li>เก็บคีย์ใน secret manager ของระบบปลายทาง ไม่ใช่ในโค้ดหรือไฟล์แชร์</li>
            <li>ระบบเก็บเพียงค่าแฮช จึงกู้คืนคีย์เดิมไม่ได้ ต้องสร้างใหม่</li>
            <li>เพิกถอนได้ทันที และมีผลกับการเรียกครั้งถัดไป</li>
            <li>ทุกครั้งที่ใช้คีย์ ระบบบันทึกเวลาที่ใช้ล่าสุดไว้ให้ตรวจสอบ</li>
          </ul>
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="card-label">คีย์ทั้งหมด</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} คีย์</span>
        </div>

        {keys.isLoading ? <Skel h={160} /> : rows.length === 0 ? (
          <div className="card card-pad dim" style={{ fontSize: 12.5 }}>
            ยังไม่มีคีย์ — ตั้งชื่อแล้วกด “สร้างคีย์” เพื่อเชื่อมต่อระบบแรก
          </div>
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>ชื่อ</th><th>คำนำหน้า</th><th>สิทธิ์</th><th>ใช้ล่าสุด</th>
                  <th>สร้างเมื่อ</th><th>สถานะ</th><th />
                </tr>
              </thead>
              <tbody>
                {rows.map((k) => (
                  <tr key={k.id} style={{ cursor: 'default' }}>
                    <td>{k.name}</td>
                    <td className="mono">aai_{k.prefix}_…</td>
                    <td>
                      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {k.scopes.map((s) => <span key={s} className="chip">{scopeLabel[s] ?? s}</span>)}
                      </span>
                    </td>
                    <td className="dim">{k.last_used_at ? thaiDateTime(k.last_used_at) : 'ยังไม่เคยใช้'}</td>
                    <td className="dim">{thaiDate(k.created_at)}</td>
                    <td>
                      <span className={`chip${k.revoked_at ? '' : ' gold'}`}>
                        {k.revoked_at ? 'เพิกถอนแล้ว' : 'ใช้งานได้'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {k.revoked_at ? <span className="dim">—</span> : confirmId === k.id ? (
                        <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-sm" disabled={revoke.isPending}
                            style={{ color: 'var(--color-danger, #c97a7a)' }}
                            onClick={() => revoke.mutate(k.id, { onSettled: () => setConfirmId(null) })}>
                            {revoke.isPending ? 'กำลังเพิกถอน…' : 'ยืนยัน'}
                          </button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(null)}>ยกเลิก</button>
                        </span>
                      ) : (
                        <button className="btn btn-ghost btn-sm" onClick={() => setConfirmId(k.id)}>เพิกถอน</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {revoke.isError ? (
          <div className="form-error" style={{ marginTop: 10 }}>
            {revoke.error instanceof Error ? revoke.error.message : 'เพิกถอนไม่สำเร็จ'}
          </div>
        ) : null}
      </div>

      {created ? (
        <div onClick={closeCreated} style={{
          position: 'fixed', inset: 0, zIndex: 40, background: 'var(--color-scrim)', backdropFilter: 'blur(3px)',
        }}>
          <div onClick={(e) => e.stopPropagation()} className="card rise" style={{
            position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
            width: 'min(560px, 94vw)', padding: 26, background: 'var(--color-surface-1)',
            boxShadow: 'var(--shadow-sheet)', border: '1px solid var(--color-hairline)',
          }}>
            <div className="eyebrow">คีย์ใหม่พร้อมใช้งาน</div>
            <div className="heading-m" style={{ marginTop: 8 }}>คัดลอกคีย์นี้ก่อนปิด</div>
            <p className="dim" style={{ fontSize: 12.5, marginTop: 10, lineHeight: 1.7 }}>
              ระบบเก็บเพียงค่าแฮชของคีย์ จึงแสดงคีย์เต็มได้ครั้งเดียวเท่านั้น
              หากทำหายต้องสร้างคีย์ใหม่และเพิกถอนอันเดิม
            </p>

            <div className="mono" style={{
              marginTop: 16, padding: '12px 14px', fontSize: 13, letterSpacing: '.02em', wordBreak: 'break-all',
              border: '1px solid var(--color-line-strong)', borderRadius: 6,
            }}>
              {created.key}
            </div>

            <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap' }}>
              {created.api_key.scopes.map((s) => <span key={s} className="chip">{scopeLabel[s] ?? s}</span>)}
            </div>

            <div className="row" style={{ marginTop: 20, gap: 8 }}>
              <button className="btn btn-primary" onClick={() => void copyKey()}>
                {copied ? 'คัดลอกแล้ว' : 'คัดลอกคีย์'}
              </button>
              <button className="btn btn-ghost" onClick={closeCreated}>ปิด</button>
              <span className="dim" style={{ fontSize: 11.5, marginLeft: 'auto' }}>{created.api_key.name}</span>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
